import { __ } from "@/i18n";

export interface Step1Form {
  companyName: string;
  businessEmail: string;
  website: string;
}

export interface Step2Form {
  wcagStandard: string;
  conformanceStatus: string;
}

export const DEFAULT_STEP1: Step1Form = {
  companyName: "",
  businessEmail: "",
  website: typeof window !== "undefined" ? window.location.origin : "",
};

export const DEFAULT_STEP2: Step2Form = {
  wcagStandard: "WCAG 2.2 Level AA",
  conformanceStatus: "fully-conformant",
};

/** Returns translated WCAG standard options. Call at render time, not at module level. */
export function getWcagOptions() {
  return [
    { value: "WCAG 2.2 Level AA", label: __( "WCAG 2.2 Level AA", "accessibility-widget" ) },
    { value: "WCAG 2.1 Level AA", label: __( "WCAG 2.1 Level AA", "accessibility-widget" ) },
    { value: "WCAG 2.0 Level AA", label: __( "WCAG 2.0 Level AA", "accessibility-widget" ) },
    { value: "Other",             label: __( "Other",             "accessibility-widget" ) },
  ];
}

/** Returns translated conformance options. Call at render time, not at module level. */
export function getConformanceOptions() {
  return [
    {
      value: "fully-conformant",
      label: __( "Fully conformant", "accessibility-widget" ),
      description: __( "The content fully conforms to the selected accessibility standard.", "accessibility-widget" ),
    },
    {
      value: "partially-conformant",
      label: __( "Partially conformant", "accessibility-widget" ),
      description: __( "Some parts of the content do not fully conform to the selected accessibility standard.", "accessibility-widget" ),
    },
    {
      value: "non-conformant",
      label: __( "Non conformant", "accessibility-widget" ),
      description: __( "The content does not conform to the selected accessibility standard.", "accessibility-widget" ),
    },
    {
      value: "not-assessed",
      label: __( "Not assessed", "accessibility-widget" ),
      description: __( "The content has not been evaluated or results are not available.", "accessibility-widget" ),
    },
  ];
}

/**
 * Returns the translated HTML statement template. Placeholders: {{company_name}},
 * {{website_url}}, {{accessibility_standard}}, {{conformance_status}},
 * {{contact_email}}, {{last_updated}}.
 *
 * Must be called at render time (inside a component or event handler) so that
 * WordPress translations are already loaded.
 */
export function getStatementTemplate(): string {
  return [
    `<h2>${ __( "Accessibility Commitment", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "{{company_name}} is committed to improving the accessibility and usability of {{website_url}} for all users, including people with disabilities. We aim to provide an inclusive digital experience and continuously work toward enhancing accessibility across our website.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Accessibility Target Standard", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "We aim to align our website with <strong>{{accessibility_standard}}</strong> accessibility standards.", "accessibility-widget" ) }</p>`,
    `<p>${ __( "This target standard guides our accessibility efforts and ongoing improvements.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Current Conformance Status", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "Based on our internal review, this website is <strong>{{conformance_status}}</strong> with <strong>{{accessibility_standard}}</strong>.", "accessibility-widget" ) }</p>`,
    `<p>${ __( "This status reflects our evaluation at the time of publication and is based on our internal review and available information. Accessibility is an ongoing process, and conformance levels may evolve as updates are implemented.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Accessibility Features", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "To support accessibility, this website includes the AccessiYes accessibility interface. This interface allows users to adjust certain visual and usability settings according to their individual needs, such as:", "accessibility-widget" ) }</p>`,
    `<ul>`,
    `<li>${ __( "Text size and spacing adjustments", "accessibility-widget" ) }</li>`,
    `<li>${ __( "Contrast and color settings", "accessibility-widget" ) }</li>`,
    `<li>${ __( "Highlighting tools", "accessibility-widget" ) }</li>`,
    `<li>${ __( "Other assistive viewing options", "accessibility-widget" ) }</li>`,
    `</ul>`,
    `<p>${ __( "These tools are intended to improve user experience, but do not replace the need for accessible content and proper underlying website structure.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Limitations and Technical Considerations", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "Please note the following:", "accessibility-widget" ) }</p>`,
    `<p>${ __( "The availability and effectiveness of accessibility features depend on the website's configuration and ongoing maintenance.", "accessibility-widget" ) }</p>`,
    `<p>${ __( "While we strive to ensure accessibility across <strong>{{website_url}}</strong>, some content or features may not yet fully meet the selected accessibility standards. Some content may be provided by third parties or affected by technical constraints beyond our immediate control.", "accessibility-widget" ) }</p>`,
    `<p>${ __( "Additionally, there may be certain legacy content or integrations that may not yet fully align with the selected accessibility standard. We are actively working to identify and address accessibility barriers within our control.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Feedback and Contact", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "If you experience any difficulty accessing content on this website or would like to report an accessibility issue, please contact us:", "accessibility-widget" ) }</p>`,
    `<p>${ __( "Email:", "accessibility-widget" ) } <a href="mailto:{{contact_email}}">{{contact_email}}</a></p>`,
    `<p>${ __( "When contacting us, please include the specific page URL and a description of the issue so we can investigate and respond appropriately.", "accessibility-widget" ) }</p>`,
    `<h2>${ __( "Continuous Improvement", "accessibility-widget" ) }</h2>`,
    `<p>${ __( "Accessibility is an ongoing effort. We regularly review our website and evaluate opportunities to improve accessibility and usability.", "accessibility-widget" ) }</p>`,
    `<p><em>${ __( "Last updated: {{last_updated}}", "accessibility-widget" ) }</em></p>`,
  ].join("\n");
}

/** @deprecated Use {@link getWcagOptions} instead. */
export const WCAG_OPTIONS = [
  { value: "WCAG 2.2 Level AA", label: "WCAG 2.2 Level AA" },
  { value: "WCAG 2.1 Level AA", label: "WCAG 2.1 Level AA" },
  { value: "WCAG 2.0 Level AA", label: "WCAG 2.0 Level AA" },
  { value: "Other", label: "Other" },
];

/** @deprecated Use {@link getConformanceOptions} instead. */
export const CONFORMANCE_OPTIONS = [
  { value: "fully-conformant",    label: "Fully conformant",    description: "The content fully conforms to the selected accessibility standard." },
  { value: "partially-conformant", label: "Partially conformant", description: "Some parts of the content do not fully conform to the selected accessibility standard." },
  { value: "non-conformant",      label: "Non conformant",      description: "The content does not conform to the selected accessibility standard." },
  { value: "not-assessed",        label: "Not assessed",        description: "The content has not been evaluated or results are not available." },
];
